/**
 * Review Screen -- one review for the complete trip experience.
 *
 * The signed-in participant can be either the guest or the host. There is one
 * required overall rating, one optional comment, and one submit action.
 */
import { useState } from 'react'
import { View, Text } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, NotificationType, selection } from '@/lib/utils/haptics'
import { RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, TEXT_STYLES, useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useAuth } from '@/lib/hooks/use-auth'
import { useBooking } from '@/lib/hooks/use-booking'
import { useReviewEligibility, useSubmitTripReview } from '@/lib/hooks/use-reviews'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { Avatar } from '@/components/ui/Avatar'
import { Image } from '@/components/ui/Image'
import { Rating } from '@/components/ui/Rating'
import { Pressable } from '@/components/ui/Pressable'
import { ChevronDown, ChevronUp } from '@/components/ui/icons'
import { AnimatedSpinner } from '@/components/ui/AnimatedSpinner'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'

const MAX_COMMENT = 1000

export default function ReviewScreen() {
  const { bookingId } = useLocalSearchParams<{ bookingId: string }>()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()
  const { user, isSessionLoading } = useAuth()

  const { data: booking, isLoading: bookingLoading, error: bookingError } = useBooking(bookingId)
  const {
    data: eligibility,
    isLoading: eligibilityLoading,
    error: eligibilityError,
  } = useReviewEligibility(bookingId)
  const { submitTripReview, isPending } = useSubmitTripReview()

  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [isCommentOpen, setIsCommentOpen] = useState(false)

  const viewerRole =
    booking && user?.id === booking.guestId
      ? 'guest'
      : booking && user?.id === booking.hostId
        ? 'host'
        : null
  const counterpartyId =
    viewerRole === 'guest' ? booking?.hostId : viewerRole === 'host' ? booking?.guestId : null
  const counterpartyName =
    (viewerRole === 'guest'
      ? booking?.hostDisplayName
      : viewerRole === 'host'
        ? booking?.guestDisplayName
        : null) ?? t('reviews.reviewer_fallback')
  const counterpartyAvatar =
    viewerRole === 'guest'
      ? booking?.hostAvatarUrl
      : viewerRole === 'host'
        ? booking?.guestAvatarUrl
        : null

  const isLoading = isSessionLoading || bookingLoading || eligibilityLoading
  const canSubmit =
    rating >= 1 && viewerRole !== null && eligibility?.canReview === true && !isPending
  const ineligibleMessage =
    eligibility?.reason === 'already_reviewed'
      ? t('reviews.error_already_reviewed')
      : eligibility?.reason === 'window_closed'
        ? t('reviews.error_window_closed')
        : eligibility?.reason === 'wrong_role' || viewerRole === null
          ? t('reviews.error_wrong_role')
          : t('reviews.error_not_eligible')

  const handleCommentToggle = () => {
    setIsCommentOpen((open) => !open)
    void selection()
  }

  const handleSubmit = async () => {
    if (!canSubmit || !booking) return

    try {
      await submitTripReview({
        rentalId: booking.id,
        rating,
        comment: comment.trim().length > 0 ? comment.trim() : undefined,
      })
      void notification(NotificationType.Success)
      router.replace('/(tabs)/trips')
    } catch (err) {
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('reviews.error_failed')))
      captureError(err, { screen: 'booking/review', action: 'submit-trip-review' })
    }
  }

  if (isLoading) {
    return (
      <ScreenContainer variant="reading" scroll={false}>
        <ScreenHeader
          title={t('reviews.title')}
          backTestID="review.back-button"
          style={{ paddingHorizontal: SPACING.md }}
        />
        <View
          testID="review.loading"
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <AnimatedSpinner size="lg" />
        </View>
      </ScreenContainer>
    )
  }

  if (bookingError || eligibilityError || !booking || !eligibility || !user) {
    return (
      <ScreenContainer variant="reading" scroll={false}>
        <ScreenHeader
          title={t('reviews.title')}
          backTestID="review.back-button"
          style={{ paddingHorizontal: SPACING.md }}
        />
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            padding: SPACING.xl,
            gap: SPACING.md,
          }}
        >
          <View testID="screen-state.unavailable" style={{ width: 0, height: 0 }} />
          <Text
            style={{
              ...TEXT_STYLES.h2,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
            accessibilityRole="header"
          >
            {t('reviews.unavailable_title')}
          </Text>
          <Text
            style={{
              ...TEXT_STYLES.bodySm,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t('reviews.unavailable_body')}
          </Text>
          <Button
            title={t('reviews.back_to_trips')}
            variant="primary"
            size="lg"
            onPress={() => router.replace('/(tabs)/trips')}
            testID="review.back-to-trips"
          />
        </View>
      </ScreenContainer>
    )
  }

  if (!eligibility.canReview || viewerRole === null) {
    return (
      <ScreenContainer variant="reading" scroll={false}>
        <ScreenHeader
          title={t('reviews.title')}
          backTestID="review.back-button"
          style={{ paddingHorizontal: SPACING.md }}
        />
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            padding: SPACING.xl,
            gap: SPACING.lg,
          }}
        >
          <Text
            style={{
              ...TEXT_STYLES.body,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {ineligibleMessage}
          </Text>
          <Button
            title={t('reviews.back_to_trips')}
            variant="primary"
            size="lg"
            onPress={() => router.replace('/(tabs)/trips')}
            testID="review.ineligible-back-to-trips"
          />
        </View>
      </ScreenContainer>
    )
  }

  const tripContext = t(
    viewerRole === 'guest' ? 'reviews.trip_context_guest' : 'reviews.trip_context_host',
    { name: counterpartyName, vehicle: booking.vehicleTitle },
  )

  return (
    <ScreenContainer variant="reading" scroll={false}>
      <ScreenHeader
        title={t('reviews.title')}
        backTestID="review.back-button"
        style={{ paddingHorizontal: SPACING.md }}
      />

      <KeyboardAwareView
        includeTopInset={false}
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: SPACING.lg,
          paddingTop: SPACING.lg,
          paddingBottom: SPACING.xl,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={{ alignItems: 'center' }}>
          <View style={{ paddingRight: SPACING.md, paddingBottom: SPACING.sm }}>
            <Image
              source={booking.vehiclePrimaryImageUrl}
              width={104}
              height={76}
              contentFit="cover"
              borderRadius={RADIUS.lg}
              accessibilityLabel={booking.vehicleTitle}
            />
            <View
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                padding: 2,
                borderRadius: RADIUS.full,
                backgroundColor: colors.background,
              }}
            >
              <Avatar
                source={counterpartyAvatar}
                fallback={counterpartyName}
                colorSeed={counterpartyId}
                size="sm"
                accessibilityLabel={counterpartyName}
              />
            </View>
          </View>
          <Text
            style={{
              ...TEXT_STYLES.bodySm,
              color: colors.textSecondary,
              textAlign: 'center',
              marginTop: SPACING.md,
            }}
          >
            {tripContext}
          </Text>
          <Text
            style={{
              ...TEXT_STYLES.h2,
              color: colors.textPrimary,
              textAlign: 'center',
              marginTop: SPACING.sm,
            }}
            accessibilityRole="header"
          >
            {t('reviews.trip_prompt')}
          </Text>
        </View>

        <View style={{ alignItems: 'center', marginTop: SPACING.xl }}>
          <Text
            style={{
              ...TEXT_STYLES.bodySm,
              color: colors.textSecondary,
              marginBottom: SPACING.sm,
            }}
          >
            {t('reviews.overall_rating')}
          </Text>
          <Rating
            value={rating}
            onChange={setRating}
            size={SPACING.xl}
            testID="review.overall-rating"
          />
          <Text
            style={{
              ...TEXT_STYLES.bodySm,
              color: rating > 0 ? colors.textPrimary : colors.textMuted,
              textAlign: 'center',
              marginTop: SPACING.sm,
              minHeight: SPACING.lg,
            }}
            accessibilityLiveRegion="polite"
          >
            {rating > 0 ? t(`reviews.rating_${rating}`) : t('reviews.tap_to_rate')}
          </Text>
        </View>

        <View style={{ marginTop: SPACING.lg }}>
          <Pressable
            onPress={handleCommentToggle}
            accessibilityRole="button"
            accessibilityLabel={
              isCommentOpen ? t('reviews.hide_comment') : t('reviews.add_comment')
            }
            accessibilityState={{ expanded: isCommentOpen }}
            testID="review.comment-toggle"
            style={({ pressed }) => ({
              minHeight: INTERACTION.minTouchTarget,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: SPACING.md,
              paddingVertical: SPACING.sm,
              borderRadius: RADIUS.full,
              backgroundColor: colors.surfaceSubtle,
              opacity: pressed ? 0.72 : 1,
            })}
          >
            <Text style={{ ...TEXT_STYLES.bodySm, color: colors.textPrimary }}>
              {isCommentOpen ? t('reviews.hide_comment') : t('reviews.add_comment')}
            </Text>
            {isCommentOpen ? (
              <ChevronUp size={20} color={colors.textSecondary} />
            ) : (
              <ChevronDown size={20} color={colors.textSecondary} />
            )}
          </Pressable>

          {isCommentOpen ? (
            <View style={{ marginTop: SPACING.md }}>
              <TextInput
                label={t('reviews.comment_label')}
                value={comment}
                onChangeText={(text) => setComment(text.slice(0, MAX_COMMENT))}
                placeholder={t('reviews.comment_placeholder')}
                multiline
                numberOfLines={4}
                maxLength={MAX_COMMENT}
                autoFocus
                testID="review.comment-input"
                accessibilityLabel={t('reviews.comment_label')}
              />
              <Text
                style={{
                  ...TEXT_STYLES.caption,
                  color: colors.textMuted,
                  textAlign: 'right',
                }}
              >
                {t('reviews.char_count', { count: comment.length, max: MAX_COMMENT })}
              </Text>
            </View>
          ) : null}
        </View>
      </KeyboardAwareView>

      <View
        style={{
          paddingHorizontal: SPACING.md,
          paddingTop: SPACING.sm,
          paddingBottom: SPACING.md,
          backgroundColor: colors.background,
          borderTopWidth: 1,
          borderTopColor: colors.border,
        }}
      >
        <Button
          title={t('reviews.submit')}
          variant="primary"
          size="lg"
          onPress={handleSubmit}
          disabled={!canSubmit}
          loading={isPending}
          testID="review.submit-button"
          accessibilityRole="button"
          accessibilityLabel={t('reviews.submit')}
        />
      </View>
    </ScreenContainer>
  )
}
